import { PatientEncounter } from '../../patient/components/models/patient-encounter';
import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { disabledConvertMessages } from '../../shared/enums';
import { Program } from '@finpay-payment-v3/payment-types';
import { StripePaymentMethod } from '../../patient/components/models/stripe-payment-method';
import {
  checkIOCComplete,
  checkDemographicsComplete,
  checkAccountHolderAddress,
  checkPFRMatch,
  checkNotSkeletonProgram,
  checkDownPaymentWillBeSatisfied,
  checkRecurringPaymentDate,
  checkRecurringPaymentDateNotPast,
  checkRecurringPaymentDateWithin60Days,
  checkRecurringPaymentDateValidDay,
  NewGuarantorFormData,
  getMissingAccountHolderFields,
  getMissingDemographicFields,
  getMissingIOCFields,
} from './validation-checks';

export interface PreEnrollRequirement {
  requirementName: string;
  status: boolean;
  missingFields?: string[];
}

export interface PreEnrollValidationParams {
  encounter: PatientEncounter | null;
  patient: PatientViewModel | null;
  program?: Program | any;
  paymentMethods?: StripePaymentMethod[];
  isDownPaymentFullyPaid?: boolean;
  isLoading?: boolean;
  recurringPaymentMethod?: StripePaymentMethod | null;
  currentPaymentAmount?: number;
  pendingRemainingBalance?: number;
  recurringPaymentDate?: string;
  isCreatingNewGuarantor?: boolean;
  newGuarantorData?: NewGuarantorFormData;
  accountHolderName?: string;
}

export interface PreEnrollValidationResult {
  isValid: boolean;
  requirements: PreEnrollRequirement[];
  failedRequirements: PreEnrollRequirement[];
}

export const validatePreEnrollRequirements = (
  params: PreEnrollValidationParams
): PreEnrollValidationResult => {
  const {
    encounter,
    patient,
    isDownPaymentFullyPaid = false,
    isLoading = false,
    recurringPaymentMethod,
    currentPaymentAmount = 0,
    pendingRemainingBalance,
    recurringPaymentDate,
    isCreatingNewGuarantor,
    newGuarantorData,
    accountHolderName,
  } = params;

  const accountHolderStatus = checkAccountHolderAddress(encounter, patient, isCreatingNewGuarantor, newGuarantorData);
  const iocStatus = checkIOCComplete(encounter);
  const demographicsStatus = checkDemographicsComplete(patient);

  const accountHolderMessage = accountHolderName 
    ? `Information for ${accountHolderName} is incomplete`
    : disabledConvertMessages.accountHolderAddress;

  const requirements: PreEnrollRequirement[] = [
    {
      requirementName: accountHolderMessage,
      status: accountHolderStatus,
      missingFields: !accountHolderStatus ? getMissingAccountHolderFields(encounter, patient, isCreatingNewGuarantor, newGuarantorData) : undefined,
    },
    {
      requirementName: disabledConvertMessages.iocLoadingLabel,
      status: !isLoading,
    },
    // {
    //   requirementName: disabledConvertMessages.championLabel,
    //   status: checkChampionExists(encounter),
    // },
    // {
    //   requirementName: disabledConvertMessages.paymentProgramLabel,
    //   status: checkPaymentProgramExists(encounter),
    // },
    // {
    //   requirementName: disabledConvertMessages.downPaymentLabel,
    //   status: checkDownPaymentStatus(encounter, isDownPaymentFullyPaid),
    // },
    // {
    //   requirementName: disabledConvertMessages.recurringPaymentLabel,
    //   status: checkRecurringPaymentExists(encounter, recurringPaymentMethod),
    // },
    {
      requirementName: disabledConvertMessages.downPaymentInsufficientLabel,
      status: checkDownPaymentWillBeSatisfied(encounter, currentPaymentAmount, pendingRemainingBalance),
    },
    {
      requirementName: disabledConvertMessages.skeletonProgramLabel,
      status: checkNotSkeletonProgram(encounter),
    },
    {
      requirementName: disabledConvertMessages.iocCompletedLabel,
      status: iocStatus,
      missingFields: !iocStatus ? getMissingIOCFields(encounter) : undefined,
    },
    {
      requirementName: disabledConvertMessages.demographicsLabel,
      status: demographicsStatus,
      missingFields: !demographicsStatus ? getMissingDemographicFields(patient) : undefined,
    },
    // {
    //   requirementName: disabledConvertMessages.riskClassLabel,
    //   status: checkRiskClassExists(encounter),
    // },
    {
      requirementName: disabledConvertMessages.pfrMissmatchLable,
      status: checkPFRMatch(encounter),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDateLabel,
      status: checkRecurringPaymentDate(encounter, recurringPaymentDate),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDatePastLabel,
      status: checkRecurringPaymentDateNotPast(encounter, recurringPaymentDate),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDateTooFarLabel,
      status: checkRecurringPaymentDateWithin60Days(encounter, recurringPaymentDate),
    },
    {
      requirementName: disabledConvertMessages.recurringPaymentDateInvalidDayLabel,
      status: checkRecurringPaymentDateValidDay(encounter, recurringPaymentDate),
    },
  ];

  const failedRequirements = requirements.filter((req) => !req.status);
  const isValid = failedRequirements.length === 0;

  return {
    isValid,
    requirements,
    failedRequirements,
  };
};

/**
 * Quick check to see if all pre-enroll requirements are met.
 * Use this for simple boolean checks without needing the full requirements list.
 */
export const arePreEnrollRequirementsMet = (
  params: PreEnrollValidationParams
): boolean => {
  return validatePreEnrollRequirements(params).isValid;
};
